import type {Asset,Draft,Surface} from '../shared/types';
import {BLOCK_WIDTH} from '../shared/sceneDimensions';
import {fitDraft,normalizeOpacity,normalizeRotation} from '../shared/paint';

/** Workflow intent is independent of raster kind: a text stamp is a drawing. */
export type ArtworkSource='stamp'|'image'|'reusable';
export interface PanelChoice {surface:Surface;point:{x:number;y:number}}
export function placementForPanel(asset:Asset,{surface,point}:PanelChoice,blockStart=surface.blockId*BLOCK_WIDTH):Draft {
  const fitted=fitDraft(surface,asset,0,0);
  return {...fitDraft(surface,asset,point.x-blockStart-surface.x-fitted.width/2,point.y-surface.y-fitted.height/2),paintOrder:'over'};
}
export function retainsReadyStamp(source:ArtworkSource|null):boolean {return source==='stamp';}

export function panelClickIntent(hasArtwork:boolean,selectingPanel:boolean):'placement'|'chooser'|'details' {
  return hasArtwork?'placement':selectingPanel?'chooser':'details';
}
export interface PlacementAttempt {key:string;draft:Draft}
/** Interrupted confirmations retain their original request across identity changes. */
export function preparePlacementAttempt(current:PlacementAttempt|null,draft:Draft,key:()=>string):PlacementAttempt {
  return current||{key:key(),draft:{...draft}};
}

export interface PlacementSettings {edge:number;rotation:number;opacity:number}
/** Placement gestures update next-operation readiness, never existing authored art.
 * Position-only movement retains the same setup object and avoids redundant UI updates. */
export function rememberPlacementSettings<T extends PlacementSettings>(previous:T,draft:Pick<Draft,'width'|'height'|'rotation'|'opacity'>):T {
 const edge=Math.max(draft.width,draft.height),rotation=normalizeRotation(draft.rotation),opacity=normalizeOpacity(draft.opacity);
 if(![edge,rotation,opacity].every(Number.isFinite)||edge<=0)return previous;
 return previous.edge===edge&&previous.rotation===rotation&&previous.opacity===opacity?previous:{...previous,edge,rotation,opacity};
}

/** Compact ready-source controls do not disable direct placement manipulation.
 * Only missing readiness, a required chooser, or active preparation blocks it. */
export function sourcePreparationBlocksCanvas(expanded:boolean,ready:boolean,preparationBlocks:boolean):boolean {
 return expanded&&(!ready||preparationBlocks);
}
